import { useInPersonEventInformation } from '@hooks/useInPersonEventInformation';
import { QrCodeIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import CheckinButton from '@molecules/CheckinButton';
import { useGetTicketTypesByEventId } from '@sunrise-ui/api-client';
import { TicketReceiptModal, useTicketPurchase } from '@sunrise-ui/composites';
import {
  Button,
  ButtonVariants,
  Colors,
  TextReg,
  isNative,
  useDynamicStyles,
  useUserPermissions,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  eventId: string;
};

export function InPersonEventButton({ eventId }: Props) {
  const styles = useDynamicStyles(createStyles);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const { isOwner, isEditor, isCheckIn } = useUserPermissions();

  const { ticket, isToday, isLoadingEvent, isPast, event, isLoadingTickets } =
    useInPersonEventInformation(eventId);

  const {
    data: nonOnSaleTicketTypes,
    isLoading: isLoadingNonOnSaleTicketTypes,
  } = useGetTicketTypesByEventId({
    eventId,
    isOnSale: false,
  });

  const { data: onSaleTicketTypes, isLoading: isLoadingAvailableTicketTypes } =
    useGetTicketTypesByEventId({
      eventId,
      isOnSale: true,
    });

  const isLoadingTicketTypes =
    isLoadingNonOnSaleTicketTypes || isLoadingAvailableTicketTypes;
  const hasAnyTicketTypes = Boolean(
    nonOnSaleTicketTypes?.length || onSaleTicketTypes?.length,
  );

  const { executePurchase, isPurchasing, isLoadingPurchase } =
    useTicketPurchase({
      eventId,
    });

  const communityId = event?.community_id;
  const isExternallyTicketed = Boolean(event?.ticketing_platform_url);
  const canCheckInAttendees =
    communityId &&
    !isExternallyTicketed &&
    (isOwner(communityId) || isEditor(communityId) || isCheckIn(communityId));

  const handleStaffCheckIn = () => {
    router.navigate(`/check-in/${eventId}`);
  };

  if (
    isLoadingEvent ||
    isLoadingTicketTypes ||
    isLoadingTickets ||
    isLoadingPurchase
  )
    return (
      <Button
        loading={true}
        size="small"
        disabled={true}
        labelStyle={styles.loadingButtonLabel}
      >
        {''}
      </Button>
    );

  if (canCheckInAttendees) {
    return (
      <Button
        size="small"
        variant={ButtonVariants.PINK}
        onPress={handleStaffCheckIn}
        labelStyle={styles.buttonLabel}
      >
        <View style={styles.checkInButtonContent}>
          <HugeiconsIcon icon={QrCodeIcon} size={14} color={Colors.text} />
          <TextReg>Check In</TextReg>
        </View>
      </Button>
    );
  }

  // Don't show if no ticket types exist at all
  if (!hasAnyTicketTypes) {
    return null;
  }

  // Internal ticketing without ticket
  if (!ticket) {
    if (!isPast)
      return (
        <Button
          size="small"
          variant={ButtonVariants.PURPLE}
          onPress={async () => {
            await executePurchase();
          }}
          loading={isPurchasing}
          disabled={isPurchasing}
          hideChildrenOnLoading={true}
          labelStyle={styles.buttonLabel}
        >
          <TextReg>Get Tickets</TextReg>
        </Button>
      );
  }

  // Internal ticketing with ticket
  if (ticket) {
    if (isToday)
      return <CheckinButton eventId={eventId} token={ticket.token} />;

    if (!isPast)
      return (
        <>
          <Button
            size="small"
            variant={ButtonVariants.WHITE}
            onPress={() => setShowTicketModal(true)}
            labelStyle={styles.buttonLabel}
          >
            <TextReg>View Ticket</TextReg>
          </Button>
          <TicketReceiptModal
            visible={showTicketModal}
            onDismiss={() => setShowTicketModal(false)}
            ticket={ticket}
            event={event}
          />
        </>
      );
  }

  return null;
}

const createStyles = () =>
  StyleSheet.create({
    highlight: {
      shadowColor: Colors.pink0opacity50,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 1,
      shadowRadius: isNative ? 8 : 15,
    },
    goingText: {
      display: 'flex',
      flexDirection: 'row',
      gap: 4,
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: 30,
    },
    loadingButtonLabel: {
      marginLeft: 16,
      marginRight: 16,
    },
    buttonLabel: { marginHorizontal: 12 },
    checkInButtonContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
  });
